// A geometria do editor de enquadramento de foto, separada do componente pra
// poder ser medida sem navegador.
//
// ⚠️ Este arquivo existe porque a mesma conta ja regrediu DUAS vezes escrita
// solta no meio do JSX: a [DA-479] (recorte cravado em 1200x1200, que destruia
// todo master que entrava pela ficha) e a [DA-525] (saida forcada a quadrado,
// que decepava produto comprido, mais o `cropSize` que comia 8% de TODA foto).
// Nas duas o typecheck e o build passaram verdes — quem media era o olho. O
// portao do deploy roda estes testes, entao a terceira nao passa calada.

// A regua do servidor (`analisarMaster`): 2000px no maior lado. Repetida aqui
// pro editor poder dizer, ENQUANTO o operador enquadra, se o recorte passa.
export const LADO_MINIMO_MASTER = 2000;

export interface Medida {
  largura: number;
  altura: number;
}

/**
 * A caixa que ENVOLVE a imagem depois de girada.
 *
 * Mesma formula do `rotateSize` do react-easy-crop, de proposito: e ela que
 * decide a proporcao da moldura (`aspect`) e o canvas do recorte, e os dois tem
 * que falar do mesmo retangulo.
 */
export function caixaGirada(largura: number, altura: number, graus: number): Medida {
  const rad = (graus * Math.PI) / 180;
  return {
    largura: Math.abs(Math.cos(rad) * largura) + Math.abs(Math.sin(rad) * altura),
    altura: Math.abs(Math.sin(rad) * largura) + Math.abs(Math.cos(rad) * altura),
  };
}

/**
 * A proporcao que o quadro de recorte tem que ter — a da foto **JA GIRADA**.
 *
 * ⚠️ E ela que faz o quadro CRESCER pra comportar a imagem inclinada. Com a
 * proporcao da foto CRUA, o react-easy-crop encaixa uma moldura de forma fixa
 * dentro da caixa da foto girada e o quadro ENCOLHE — girar passa a custar
 * resolucao, e o "Aplicar" recusa (ver `saidaNoZoom1`).
 */
export function proporcaoDaMoldura(proporcaoCrua: number, graus: number): number {
  const c = caixaGirada(proporcaoCrua, 1, graus);
  return c.largura / c.altura;
}

/**
 * O que sai do editor no zoom 1, em pixels da ORIGEM.
 *
 * Com a moldura na proporcao da foto girada, no zoom 1 ela cobre exatamente a
 * caixa da imagem girada — entao a saida E essa caixa. Consequencias que os
 * testes fixam: no giro 0 sai a foto INTEIRA e INTACTA (a garantia da DA-525),
 * e girar nunca decepa o produto.
 *
 * ⚠️ Num angulo torto a tarja da tela pode mostrar **1px de diferenca** disto: o
 * numero de la vem do `croppedAreaPixels` do react-easy-crop, que arredonda em
 * dois passos (porcentagem e depois pixel). Nao e divergencia — nos angulos que
 * importam pro `semAlteracao` (giro 0) o seno e o cosseno sao exatos e os dois
 * caminhos dao o MESMO numero. Nao apertar essa folga sem medir na tela.
 */
export function saidaNoZoom1(medida: Medida, graus: number): Medida {
  const c = caixaGirada(medida.largura, medida.altura, graus);
  return { largura: Math.round(c.largura), altura: Math.round(c.altura) };
}

/** O recorte passa na regua do master? (maior lado >= 2000px) */
export function alcancaMaster(medida: Medida): boolean {
  return Math.max(medida.largura, medida.altura) >= LADO_MINIMO_MASTER;
}

export interface Retangulo extends Medida {
  x: number;
  y: number;
}

/**
 * O pedaco do recorte que tem FOTO de verdade — a interseccao com a caixa da
 * imagem girada.
 *
 * ⚠️ Abaixo do zoom 1 a moldura fica MAIOR que a imagem e o `croppedAreaPixels`
 * conta a margem vazia junto. O servidor nao conta: o `gerarMasterWebp` roda
 * `-trim` e mede o PRODUTO antes da regua. Sem aparar aqui, a tarja ficava
 * amarela e o upload voltava 422 — luz verde que termina em recusa e' pior que
 * recusa, porque manda a pessoa tentar de novo pelo mesmo caminho.
 */
export function recorteUtil(area: Retangulo, caixa: Medida): Retangulo {
  const x0 = Math.max(0, area.x);
  const y0 = Math.max(0, area.y);
  const x1 = Math.min(caixa.largura, area.x + area.largura);
  const y1 = Math.min(caixa.altura, area.y + area.altura);
  return { x: x0, y: y0, largura: Math.max(0, x1 - x0), altura: Math.max(0, y1 - y0) };
}

/**
 * A margem transparente que o `gerarMasterWebp` acrescenta em volta do produto
 * antes da regua correr (`MARGEM_MASTER` do servidor, em cada lado).
 */
export const MARGEM_MASTER = 0.03;

/**
 * O numero que o SERVIDOR vai medir, dado o produto que sobe daqui.
 *
 * ⚠️ A regua nao roda sobre o arquivo que a gente manda: o `gerarMasterWebp`
 * primeiro **apara o vazio** (`-trim`, que mede o PRODUTO, nao a tela) e depois
 * acrescenta 3% de margem em cada lado. So entao o `analisarMaster` mede o maior
 * lado. Prever qualquer coisa diferente disto e' a tela inventando uma regua
 * propria — o defeito da [DA-525], que custou uma release inteira.
 */
export function masterQueOServidorVaiVer(produto: Medida): number {
  return Math.max(produto.largura, produto.altura) / (1 - 2 * MARGEM_MASTER);
}

/**
 * Meio por cento de folga quando a compensacao entra em acao. O produto e' medido
 * num canvas REDUZIDO (pra caber em cada quadro de arrasto), entao o numero tem
 * erro de alguns pixels; sem folga, um erro de 10px em 2000 vira 422 no upload —
 * e o 422 chega depois do clique, que e' exatamente o que esta tela existe pra
 * evitar.
 */
const FOLGA_DA_MEDICAO = 1.01;

/** Teto de sanidade: nenhum enquadramento razoavel precisa dobrar de tamanho. */
const TETO_COMPENSACAO = 2;

export interface EntradaDaSaida {
  area: Retangulo;
  original: Medida;
  graus: number;
  /**
   * O PRODUTO dentro do recorte (pixels nao transparentes), em pixels da ORIGEM —
   * medido no canvas, do mesmo jeito que o `-trim` do servidor mede. Ausente
   * (canvas contaminado, imagem opaca) cai no recorte inteiro, que e' o limite
   * superior e mantem o comportamento antigo.
   */
  produtoNoRecorte?: Medida | null;
  /** O produto INTEIRO no angulo atual — quanto de caixa o giro deixou. */
  produtoGirado?: Medida | null;
  /** O produto INTEIRO sem giro — o que a foto valia parada. */
  produtoParado?: Medida | null;
}

/**
 * O que sai do editor: o pedaco a desenhar, quanto amplia-lo, o arquivo final e o
 * numero que o servidor vai ver.
 *
 * Peca unica de proposito — a tarja, o botao e o `recortarArea` tem que falar do
 * MESMO retangulo. Quando divergiram, a tela prometeu o que o servidor recusou.
 *
 * **Por que compensar**: a regua mede uma CAIXA, e a caixa de um produto fino
 * encolhe perto dos 45 graus sem que um pixel do produto se perca — girar nao
 * reamostra nada. O terminal de 524x2000 do produto 1634 cai pra 1791x1868 a 44
 * graus (medido na VPS, com o `-trim` de verdade) e a regua recusa uma foto que
 * ela mesma aprovou parada. E' artefato de medicao, nao perda de qualidade.
 *
 * **Dois portoes, e cada um fecha um jeito diferente de fabricar master:**
 *
 *   1. ⛔ **So compensa foto que passa PARADA** (`produtoParado`). Foto abaixo da
 *      regua nao passa girada em angulo nenhum — senao bastaria inclinar uma foto
 *      ruim pra ela virar master.
 *   2. ⛔ **O teto e' o que o GIRO tirou do PRODUTO** (`produtoParado` sobre
 *      `produtoGirado`), nunca "o que for preciso". Sem ele, quem desse zoom pra
 *      dentro do produto — perda ESCOLHIDA por quem enquadra — teria o recorte
 *      ampliado de volta, e um pedaco do produto viraria master.
 *
 * ⚠️ O teto se mede no PRODUTO, nao na moldura: o terminal e' afilado e, girado,
 * nao encosta nos cantos do proprio retangulo — a caixa dele cai mais do que a
 * caixa da imagem. Medido na VPS com o `-trim` de verdade, um teto calculado pela
 * moldura ficava 13px curto e o upload voltava 422.
 *
 * ⚠️ **O que a compensacao NAO e'**: ela nao devolve detalhe. Amplia ~12% no caso
 * do terminal — o mesmo gesto que a FotoTool ja faz pra chegar nos 2000
 * (`ALVO_MIN` do `processar.py`). Se um dia a regua medir o produto pelo proprio
 * eixo em vez da caixa que o envolve, a compensacao inteira vira `escala = 1` e
 * nada precisa ser interpolado.
 */
export function saidaDoEditor(entrada: EntradaDaSaida): {
  recorte: Retangulo;
  escala: number;
  saida: Medida;
  master: number;
  alcanca: boolean;
} {
  const { area, original, graus, produtoNoRecorte, produtoGirado, produtoParado } = entrada;
  const caixa = caixaGirada(original.largura, original.altura, graus);
  const recorte = recorteUtil(area, caixa);
  const masterSemEscala = masterQueOServidorVaiVer(produtoNoRecorte ?? recorte);

  // Portao 1: a foto tem que valer master PARADA.
  const passavaParado = !!produtoParado && masterQueOServidorVaiVer(produtoParado) >= LADO_MINIMO_MASTER;
  // Portao 2: o teto e' exatamente o que a inclinacao tirou do produto.
  const teto =
    produtoParado && produtoGirado && Math.max(produtoGirado.largura, produtoGirado.altura) > 0
      ? Math.max(
          1,
          Math.max(produtoParado.largura, produtoParado.altura) /
            Math.max(produtoGirado.largura, produtoGirado.altura),
        )
      : 1;

  let escala = 1;
  if (passavaParado && masterSemEscala > 0 && masterSemEscala < LADO_MINIMO_MASTER) {
    const precisa = (LADO_MINIMO_MASTER * FOLGA_DA_MEDICAO) / masterSemEscala;
    escala = Math.max(1, Math.min(precisa, teto, TETO_COMPENSACAO));
  }

  const master = masterSemEscala * escala;
  return {
    recorte,
    escala,
    saida: {
      largura: Math.round(recorte.largura * escala),
      altura: Math.round(recorte.altura * escala),
    },
    master,
    alcanca: master >= LADO_MINIMO_MASTER,
  };
}
